import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { decryptPatient, encryptPatientPayload } from "../utils/encryption";

function EditPatientPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [patient, setPatient] = useState(null);
  const [form, setForm] = useState({
    complaint: "",
    history: "",
    status: "",
    sessionFee: "",
    paymentFrequency: "per_session",
    selectedPackage: "",
    appointmentCount: "",
    paymentType: "prepaid",
    billingType: "perSession",
    phone: "",
    email: "",
    address: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("clinical");
  const [packages, setPackages] = useState([]);
  const [showBillingGuide, setShowBillingGuide] = useState(false);
  const [currentGuidStep, setCurrentGuidStep] = useState(0);
  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [appointmentFilters, setAppointmentFilters] = useState({
    status: "All",
    dateRange: "All",
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    const guideParam = params.get("guide");
    if (tabParam === "billing") {
      setActiveTab("billing");
      if (guideParam === "true") {
        setShowBillingGuide(true);
        setCurrentGuidStep(0);
      }
    } else if (tabParam === "status") {
      setActiveTab("status");
    } else {
      setActiveTab("clinical");
    }
  }, [location.search]);

  useEffect(() => {
    const load = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser || !id) {
          setLoading(false);
          return;
        }

        const userDocRef = doc(db, "users", currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          setPackages(userData.packages || []);
        }

        const docRef = doc(db, "patients", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = decryptPatient({ id: docSnap.id, ...docSnap.data() });
          setPatient(data);
          setForm({
            complaint: data.complaint || "",
            history: data.history || "",
            status: data.status || "New",
            sessionFee: data.sessionFee || "",
            paymentFrequency: data.paymentFrequency || "per_session",
            selectedPackage: data.selectedPackage || "",
            appointmentCount: data.appointmentCount || "",
            paymentType: data.paymentType || "prepaid",
            billingType: data.selectedPackage ? "package" : "perSession",
            phone: data.phone || "",
            email: data.email || "",
            address: data.address || "",
          });
        } else {
          setError("Patient not found");
        }
      } catch (err) {
        setError(err.message || "Failed to load patient");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, location.search]);

  // Load appointments when appointments tab is active
  useEffect(() => {
    const loadAppointments = async () => {
      if (activeTab !== "appointments" || !patient) return;

      try {
        setLoadingAppointments(true);
        const currentUser = auth.currentUser;
        if (!currentUser) return;

        const appointmentsQuery = query(
          collection(db, "appointments"),
          where("userId", "==", currentUser.uid),
          where("patientId", "==", id)
        );

        const querySnapshot = await getDocs(appointmentsQuery);
        const appointmentsData = querySnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((appt) => !appt.deleted);

        // Sort by date (newest first)
        appointmentsData.sort((a, b) => {
          const aTime = a.start?.toMillis
            ? a.start.toMillis()
            : new Date(a.start).getTime();
          const bTime = b.start?.toMillis
            ? b.start.toMillis()
            : new Date(b.start).getTime();
          return bTime - aTime;
        });

        setAppointments(appointmentsData);
      } catch (err) {
        console.error("Failed to load appointments:", err);
      } finally {
        setLoadingAppointments(false);
      }
    };

    loadAppointments();
  }, [activeTab, patient, id]);

  const handleSaveClinical = async (e) => {
    e.preventDefault();
    setError("");

    // Validate billing fields
    if (
      form.sessionFee &&
      (isNaN(parseFloat(form.sessionFee)) || parseFloat(form.sessionFee) < 0)
    ) {
      setError("Session fee must be a valid positive number");
      return;
    }

    if (
      form.appointmentCount &&
      (isNaN(parseInt(form.appointmentCount)) ||
        parseInt(form.appointmentCount) <= 0)
    ) {
      setError("Appointment count must be a valid positive number");
      return;
    }

    try {
      setSaving(true);
      const docRef = doc(db, "patients", id);
      const updateData = {
        complaint: form.complaint,
        history: form.history,
        paymentFrequency: form.paymentFrequency,
        paymentType: form.paymentType,
        phone: form.phone,
        email: form.email,
        address: form.address,
      };

      if (form.billingType === "package") {
        if (form.selectedPackage) {
          updateData.selectedPackage = form.selectedPackage;
        } else {
          updateData.selectedPackage = null;
        }
        if (form.appointmentCount) {
          updateData.appointmentCount = parseInt(form.appointmentCount);
        } else {
          updateData.appointmentCount = null;
        }
        if (form.selectedPackage && form.appointmentCount) {
          const selectedPkg = packages.find(
            (pkg) => pkg.name === form.selectedPackage
          );
          if (selectedPkg && selectedPkg.lumpSumValue) {
            const appointmentCount = parseInt(form.appointmentCount);
            updateData.sessionFee =
              parseFloat(selectedPkg.lumpSumValue) / appointmentCount;
          } else {
            updateData.sessionFee = null;
          }
        } else {
          updateData.sessionFee = null;
        }
      } else {
        if (form.sessionFee) {
          updateData.sessionFee = parseFloat(form.sessionFee);
        } else {
          updateData.sessionFee = null;
        }
        updateData.selectedPackage = null;
        updateData.appointmentCount = null;
      }

      // Check if any billing data is being saved
      const hasBillingData =
        updateData.sessionFee || updateData.selectedPackage;

      // Update status to "Active" if billing data is present and status is "New"
      if (hasBillingData && patient && patient.status === "New") {
        updateData.status = "Active";
      }

      const encryptedUpdate = encryptPatientPayload(updateData);
      await updateDoc(docRef, encryptedUpdate);
      setPatient((p) => ({ ...p, ...updateData }));
      navigate("/patients");
    } catch (err) {
      setError(err.message || "Failed to update patient information");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveStatus = async (e) => {
    e.preventDefault();
    setError("");
    try {
      setSaving(true);
      const docRef = doc(db, "patients", id);
      await updateDoc(docRef, {
        status: "Recovered",
      });
      setPatient((p) => ({ ...p, status: "Recovered" }));
      navigate("/patients");
    } catch (err) {
      setError(err.message || "Failed to update status");
    } finally {
      setSaving(false);
    }
  };

  const initialFromName = (name) => {
    if (!name) return "P";
    return name.trim().charAt(0).toUpperCase();
  };

  // Define guide steps - this will be filtered dynamically
  const getAllBillingGuideSteps = useCallback(
    () => [
      {
        title: "💡 Billing Type",
        message:
          "Choose between Package (fixed sessions) or Per Session Fee (charge per visit)",
        field: "billingType",
        selector: "#field-billingType",
      },
      {
        title: "📦 Treatment Package",
        message:
          "Select a pre-defined package that suits your patient. The appointment count will auto-fill!",
        field: "selectedPackage",
        condition: () => form.billingType === "package",
        selector: "#field-selectedPackage",
      },
      {
        title: "📅 Appointment Count",
        message:
          "Total number of sessions included in this package. You can adjust this as needed.",
        field: "appointmentCount",
        condition: () => form.billingType === "package",
        selector: "#field-appointmentCount",
      },
      {
        title: "💰 Per Session Fee",
        message:
          "Set the fee you charge per session when billing by the visit.",
        field: "sessionFee",
        condition: () => form.billingType === "perSession",
        selector: "#field-sessionFee",
      },
      {
        title: "🔄 Payment Frequency",
        message:
          "How often should this patient be billed? (Per Session, Weekly, or Monthly)",
        field: "paymentFrequency",
        selector: "#field-paymentFrequency",
      },
      {
        title: "✅ Payment Type",
        message:
          "Will the patient pay upfront (Prepaid) or after sessions (Postpaid)?",
        field: "paymentType",
        selector: "#field-paymentType",
      },
      {
        title: "✨ Save Your Changes",
        message:
          "Click the 'Save Billing Info' button to save all the billing details for this patient.",
        field: "saveBilling",
        selector: ".edit-patient-action-primary",
        isFinalStep: true,
      },
    ],
    [form.billingType]
  );

  // Get visible steps based on current form state
  const getVisibleGuideSteps = () => {
    return getAllBillingGuideSteps().filter(
      (step) => !step.condition || step.condition()
    );
  };

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const newForm = { ...prev, [field]: value };

      // If billingType changes while guide is active, adjust the current step
      if (field === "billingType" && showBillingGuide) {
        // Get the current step's field
        const visibleSteps = getVisibleGuideSteps();
        const currentField = visibleSteps[currentGuidStep]?.field;

        // If we're on billingType step, stay there
        if (currentField === "billingType") {
          // Don't change step
        } else {
          // Reset to step 0 to avoid issues
          setCurrentGuidStep(0);
        }
      }

      return newForm;
    });
  };

  // When a guide step is shown, smoothly bring the related field into view
  useEffect(() => {
    if (!showBillingGuide) return;
    const steps = getAllBillingGuideSteps().filter(
      (step) => !step.condition || step.condition()
    );
    const step = steps[currentGuidStep];
    if (!step) return;

    const target = document.getElementById(`field-${step.field}`);
    if (target) {
      setTimeout(() => {
        target.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }, 100);
    }
  }, [
    showBillingGuide,
    currentGuidStep,
    form.billingType,
    getAllBillingGuideSteps,
  ]);

  if (loading) {
    return (
      <div className="edit-patient-layout-new">
        <header className="edit-patient-header-new">
          <button
            className="edit-patient-back-btn"
            onClick={() => navigate("/patients")}
          >
            <span className="material-symbols-outlined">arrow_back</span>
          </button>
          <h1>Edit Patient</h1>
          <div className="edit-patient-header-spacer" />
        </header>
        <div className="edit-patient-content-new">
          <div
            className="edit-patient-card-new"
            style={{ textAlign: "center", padding: "48px 24px" }}
          >
            <p
              style={{
                color: "var(--text-muted)",
                fontSize: "15px",
                fontWeight: "600",
              }}
            >
              Loading patient details...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="edit-patient-layout-new">
        <header className="edit-patient-header-new">
          <button
            className="edit-patient-back-btn"
            onClick={() => navigate("/patients")}
          >
            <span className="material-symbols-outlined">arrow_back</span>
          </button>
          <h1>Edit Patient</h1>
          <div className="edit-patient-header-spacer" />
        </header>
        <div className="edit-patient-content-new">
          <div
            className="edit-patient-card-new"
            style={{ textAlign: "center", padding: "48px 24px" }}
          >
            <span
              className="material-symbols-outlined"
              style={{
                fontSize: "48px",
                color: "var(--text-muted)",
                marginBottom: "16px",
                display: "block",
              }}
            >
              person_off
            </span>
            <h3
              style={{
                margin: "0 0 8px",
                color: "var(--text-primary)",
                fontSize: "18px",
                fontWeight: "800",
              }}
            >
              Patient not found
            </h3>
            <p
              style={{
                color: "var(--text-muted)",
                fontSize: "15px",
                fontWeight: "500",
                margin: "0 0 24px",
              }}
            >
              The patient you're looking for doesn't exist or has been removed.
            </p>
            <button
              className="edit-patient-action-btn edit-patient-action-primary"
              onClick={() => navigate("/patients")}
            >
              <span className="material-symbols-outlined">arrow_back</span>
              Back to Patients
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-patient-layout-new">
      <header className="edit-patient-header-new">
        <button
          className="edit-patient-back-btn"
          onClick={() => navigate("/patients")}
        >
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <h1>Edit Patient</h1>
        <div className="edit-patient-header-spacer" />
      </header>

      <div className="edit-patient-content-new">
        {/* Patient Info Card */}
        <div className="edit-patient-card-new edit-patient-info-card">
          <div className="edit-patient-avatar-section">
            <div className="edit-patient-avatar-large">
              <span>{initialFromName(patient.name)}</span>
            </div>
            <div className="edit-patient-info-section">
              <h2>{patient.name || "Unknown Patient"}</h2>
              <div className="edit-patient-status-display">
                <span
                  className="edit-patient-status-badge"
                  style={{ color: getStatusColor(patient.status) }}
                >
                  {patient.status || "New"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="edit-patient-tabs-new">
          <button
            className={`edit-patient-tab-new ${
              activeTab === "clinical" ? "active" : ""
            }`}
            onClick={() => setActiveTab("clinical")}
          >
            <span className="material-symbols-outlined">description</span>
            <span>Clinical Profile</span>
          </button>
          <button
            className={`edit-patient-tab-new ${
              activeTab === "contact" ? "active" : ""
            }`}
            onClick={() => setActiveTab("contact")}
          >
            <span className="material-symbols-outlined">contacts</span>
            <span>Contact Info</span>
          </button>
          <button
            className={`edit-patient-tab-new ${
              activeTab === "appointments" ? "active" : ""
            }`}
            onClick={() => setActiveTab("appointments")}
          >
            <span className="material-symbols-outlined">event</span>
            <span>Appointment History</span>
          </button>
          {patient && patient.status === "Appointment Schedule" && (
            <button
              className={`edit-patient-tab-new ${
                activeTab === "status" ? "active" : ""
              }`}
              onClick={() => setActiveTab("status")}
            >
              <span className="material-symbols-outlined">info</span>
              <span>Update Status</span>
            </button>
          )}
        </div>

        {/* Clinical Profile Tab */}
        {activeTab === "clinical" && (
          <form className="edit-patient-form-new" onSubmit={handleSaveClinical}>
            <div className="edit-patient-form-card">
              <div className="edit-patient-section-header">
                <span className="material-symbols-outlined">
                  medical_information
                </span>
                <h3>Clinical Information</h3>
              </div>

              <div className="edit-patient-field-new">
                <label className="edit-patient-label-new">
                  <span className="material-symbols-outlined">person</span>
                  Patient Name
                </label>
                <div className="edit-patient-readonly-field">
                  {patient.name || "Unknown Patient"}
                </div>
              </div>

              <div className="edit-patient-field-new">
                <label className="edit-patient-label-new">
                  <span className="material-symbols-outlined">info</span>
                  Current Status
                </label>
                <div
                  className="edit-patient-readonly-field"
                  style={{ color: getStatusColor(patient.status) }}
                >
                  {patient.status || "New"}
                </div>
              </div>

              <div className="edit-patient-field-new">
                <label className="edit-patient-label-new">
                  <span className="material-symbols-outlined">sick</span>
                  Chief Complaint / Reason for Visit
                </label>
                <textarea
                  className="edit-patient-textarea-new"
                  placeholder="Describe the primary symptoms or injury..."
                  value={form.complaint}
                  onChange={handleChange("complaint")}
                  rows={5}
                />
              </div>

              <div className="edit-patient-field-new">
                <label className="edit-patient-label-new">
                  <span className="material-symbols-outlined">history</span>
                  Medical History (Optional)
                </label>
                <textarea
                  className="edit-patient-textarea-new"
                  placeholder="Previous surgeries, medications, allergies, etc."
                  value={form.history}
                  onChange={handleChange("history")}
                  rows={5}
                />
              </div>

              {/* Billing Section */}
              <div
                className="edit-patient-section-header"
                style={{ marginTop: "32px" }}
              >
                <span className="material-symbols-outlined">payments</span>
                <h3>Billing Information</h3>
              </div>

              <div className="edit-patient-field-new">
                <label className="edit-patient-label-new">
                  <span className="material-symbols-outlined">payments</span>
                  Billing Type
                </label>
                <select
                  className="edit-patient-select-new"
                  value={form.billingType}
                  onChange={handleChange("billingType")}
                >
                  <option value="package">Package</option>
                  <option value="perSession">Per Session Fee</option>
                </select>
                <p
                  style={{
                    marginTop: "6px",
                    fontSize: "12px",
                    color: "var(--text-muted)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "14px" }}
                  >
                    info
                  </span>
                  Choose between package or per session billing
                </p>
              </div>

              {form.billingType === "package" ? (
                <>
                  <div className="edit-patient-field-new">
                    <label className="edit-patient-label-new">
                      <span className="material-symbols-outlined">
                        inventory_2
                      </span>
                      Treatment Package
                    </label>
                    <select
                      className="edit-patient-select-new"
                      value={form.selectedPackage}
                      onChange={handleChange("selectedPackage")}
                    >
                      <option value="">Select a package</option>
                      {packages.map((pkg, index) => (
                        <option key={index} value={pkg.name}>
                          {pkg.name} ({pkg.appointmentCount} appointments - ₹
                          {pkg.lumpSumValue
                            ? parseFloat(pkg.lumpSumValue).toFixed(2)
                            : "0.00"}
                          )
                        </option>
                      ))}
                    </select>
                    <p
                      style={{
                        marginTop: "6px",
                        fontSize: "12px",
                        color: "var(--text-muted)",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <span
                        className="material-symbols-outlined"
                        style={{ fontSize: "14px" }}
                      >
                        info
                      </span>
                      Select a package to auto-fill appointment count
                    </p>
                  </div>

                  <div className="edit-patient-field-new">
                    <label className="edit-patient-label-new">
                      <span className="material-symbols-outlined">
                        event_available
                      </span>
                      Number of Appointments
                    </label>
                    <input
                      type="number"
                      className="edit-patient-input-new"
                      placeholder="e.g., 10"
                      value={form.appointmentCount}
                      onChange={handleChange("appointmentCount")}
                      min="1"
                      step="1"
                    />
                    <p
                      style={{
                        marginTop: "6px",
                        fontSize: "12px",
                        color: "var(--text-muted)",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <span
                        className="material-symbols-outlined"
                        style={{ fontSize: "14px" }}
                      >
                        info
                      </span>
                      Total number of appointments for this patient (can be
                      edited)
                    </p>
                  </div>
                </>
              ) : (
                <div className="edit-patient-field-new">
                  <label className="edit-patient-label-new">
                    <span className="material-symbols-outlined">
                      attach_money
                    </span>
                    Per Session Fee (INR)
                  </label>
                  <input
                    type="number"
                    className="edit-patient-input-new"
                    placeholder="0.00"
                    value={form.sessionFee}
                    onChange={handleChange("sessionFee")}
                    min="0"
                    step="0.01"
                  />
                  <p
                    style={{
                      marginTop: "6px",
                      fontSize: "12px",
                      color: "var(--text-muted)",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <span
                      className="material-symbols-outlined"
                      style={{ fontSize: "14px" }}
                    >
                      info
                    </span>
                    Enter the fee charged per session for this patient
                  </p>
                </div>
              )}

              <div className="edit-patient-field-new">
                <label className="edit-patient-label-new">
                  <span className="material-symbols-outlined">schedule</span>
                  Payment Frequency
                </label>
                <select
                  className="edit-patient-select-new"
                  value={form.paymentFrequency}
                  onChange={handleChange("paymentFrequency")}
                >
                  <option value="per_session">Per Session</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
                <p
                  style={{
                    marginTop: "6px",
                    fontSize: "12px",
                    color: "var(--text-muted)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "14px" }}
                  >
                    info
                  </span>
                  How often payment is collected from this patient
                </p>
              </div>

              <div className="edit-patient-field-new">
                <label className="edit-patient-label-new">
                  <span className="material-symbols-outlined">payment</span>
                  Payment Type
                </label>
                <select
                  className="edit-patient-select-new"
                  value={form.paymentType}
                  onChange={handleChange("paymentType")}
                >
                  <option value="prepaid">Pre-paid</option>
                  <option value="postpaid">Post-paid</option>
                </select>
                <p
                  style={{
                    marginTop: "6px",
                    fontSize: "12px",
                    color: "var(--text-muted)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "14px" }}
                  >
                    info
                  </span>
                  Whether payment is collected before or after appointments
                </p>
              </div>

              {/* Contact Information Section */}
              <div
                className="edit-patient-section-header"
                style={{ marginTop: "32px" }}
              >
                <span className="material-symbols-outlined">contacts</span>
                <h3>Contact Information</h3>
              </div>

              <div className="edit-patient-field-new">
                <label className="edit-patient-label-new">
                  <span className="material-symbols-outlined">phone</span>
                  Phone Number
                </label>
                <input
                  type="tel"
                  className="edit-patient-input-new"
                  placeholder="(555) 123-4567"
                  value={form.phone}
                  onChange={handleChange("phone")}
                />
                <p
                  style={{
                    marginTop: "6px",
                    fontSize: "12px",
                    color: "var(--text-muted)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "14px" }}
                  >
                    info
                  </span>
                  Patient's contact phone number
                </p>
              </div>

              <div className="edit-patient-field-new">
                <label className="edit-patient-label-new">
                  <span className="material-symbols-outlined">email</span>
                  Email Address{" "}
                  <span style={{ color: "#999", fontSize: "12px" }}>
                    (Optional)
                  </span>
                </label>
                <input
                  type="email"
                  className="edit-patient-input-new"
                  placeholder="patient@example.com"
                  value={form.email}
                  onChange={handleChange("email")}
                />
                <p
                  style={{
                    marginTop: "6px",
                    fontSize: "12px",
                    color: "var(--text-muted)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "14px" }}
                  >
                    info
                  </span>
                  Patient's email address
                </p>
              </div>

              <div className="edit-patient-field-new">
                <label className="edit-patient-label-new">
                  <span className="material-symbols-outlined">location_on</span>
                  Address{" "}
                  <span style={{ color: "#999", fontSize: "12px" }}>
                    (Optional)
                  </span>
                </label>
                <textarea
                  className="edit-patient-textarea-new"
                  placeholder="Street address, City, State, ZIP code"
                  value={form.address}
                  onChange={handleChange("address")}
                  rows={3}
                />
                <p
                  style={{
                    marginTop: "6px",
                    fontSize: "12px",
                    color: "var(--text-muted)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "14px" }}
                  >
                    info
                  </span>
                  Patient's residential address
                </p>
              </div>

              {error && (
                <div className="edit-patient-error">
                  <span className="material-symbols-outlined">error</span>
                  {error}
                </div>
              )}

              <div className="edit-patient-actions-new">
                <button
                  type="button"
                  className="edit-patient-action-btn edit-patient-action-secondary"
                  onClick={() => navigate("/patients")}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="edit-patient-action-btn edit-patient-action-primary"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="material-symbols-outlined">
                        hourglass_empty
                      </span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined">save</span>
                      Save Profile
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        )}

        {/* Appointment History Tab */}
        {activeTab === "appointments" && (
          <div className="edit-patient-form-new">
            <div className="edit-patient-form-card">
              <div className="edit-patient-section-header">
                <span className="material-symbols-outlined">event</span>
                <h3>Appointment History</h3>
              </div>

              {/* Filters */}
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  marginBottom: "24px",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ flex: "1", minWidth: "200px" }}>
                  <label
                    className="edit-patient-label-new"
                    style={{ marginBottom: "8px" }}
                  >
                    <span className="material-symbols-outlined">
                      filter_list
                    </span>
                    Status
                  </label>
                  <select
                    className="edit-patient-select-new"
                    value={appointmentFilters.status}
                    onChange={(e) =>
                      setAppointmentFilters((prev) => ({
                        ...prev,
                        status: e.target.value,
                      }))
                    }
                  >
                    <option value="All">All Statuses</option>
                    <option value="Confirmed">Confirmed</option>
                    <option value="Pending">Pending</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
                <div style={{ flex: "1", minWidth: "200px" }}>
                  <label
                    className="edit-patient-label-new"
                    style={{ marginBottom: "8px" }}
                  >
                    <span className="material-symbols-outlined">
                      date_range
                    </span>
                    Date Range
                  </label>
                  <select
                    className="edit-patient-select-new"
                    value={appointmentFilters.dateRange}
                    onChange={(e) =>
                      setAppointmentFilters((prev) => ({
                        ...prev,
                        dateRange: e.target.value,
                      }))
                    }
                  >
                    <option value="All">All Time</option>
                    <option value="Today">Today</option>
                    <option value="This Week">This Week</option>
                    <option value="This Month">This Month</option>
                    <option value="Last 3 Months">Last 3 Months</option>
                    <option value="Last 6 Months">Last 6 Months</option>
                    <option value="This Year">This Year</option>
                  </select>
                </div>
              </div>

              {/* Appointments List */}
              {loadingAppointments ? (
                <div style={{ textAlign: "center", padding: "48px 24px" }}>
                  <p
                    style={{
                      color: "var(--text-muted)",
                      fontSize: "15px",
                      fontWeight: "600",
                    }}
                  >
                    Loading appointments...
                  </p>
                </div>
              ) : (
                (() => {
                  // Filter appointments
                  const filteredAppointments = appointments.filter((appt) => {
                    // Status filter
                    if (appointmentFilters.status !== "All") {
                      const apptStatus = (
                        appt.status || "Confirmed"
                      ).toLowerCase();
                      const filterStatus =
                        appointmentFilters.status.toLowerCase();
                      if (!apptStatus.includes(filterStatus)) return false;
                    }

                    // Date range filter
                    if (appointmentFilters.dateRange !== "All") {
                      const apptDate = appt.start?.toDate
                        ? appt.start.toDate()
                        : new Date(appt.start);
                      const now = new Date();
                      const today = new Date(
                        now.getFullYear(),
                        now.getMonth(),
                        now.getDate()
                      );

                      switch (appointmentFilters.dateRange) {
                        case "Today":
                          const apptDay = new Date(
                            apptDate.getFullYear(),
                            apptDate.getMonth(),
                            apptDate.getDate()
                          );
                          if (apptDay.getTime() !== today.getTime())
                            return false;
                          break;
                        case "This Week":
                          const weekStart = new Date(today);
                          weekStart.setDate(today.getDate() - today.getDay());
                          if (apptDate < weekStart) return false;
                          break;
                        case "This Month":
                          if (
                            apptDate.getMonth() !== now.getMonth() ||
                            apptDate.getFullYear() !== now.getFullYear()
                          )
                            return false;
                          break;
                        case "Last 3 Months":
                          const threeMonthsAgo = new Date(now);
                          threeMonthsAgo.setMonth(now.getMonth() - 3);
                          if (apptDate < threeMonthsAgo) return false;
                          break;
                        case "Last 6 Months":
                          const sixMonthsAgo = new Date(now);
                          sixMonthsAgo.setMonth(now.getMonth() - 6);
                          if (apptDate < sixMonthsAgo) return false;
                          break;
                        case "This Year":
                          if (apptDate.getFullYear() !== now.getFullYear())
                            return false;
                          break;
                        default:
                          break;
                      }
                    }

                    return true;
                  });

                  if (filteredAppointments.length === 0) {
                    return (
                      <div
                        style={{ textAlign: "center", padding: "48px 24px" }}
                      >
                        <span
                          className="material-symbols-outlined"
                          style={{
                            fontSize: "48px",
                            color: "var(--text-muted)",
                            marginBottom: "16px",
                            display: "block",
                          }}
                        >
                          event_busy
                        </span>
                        <h3
                          style={{
                            margin: "0 0 8px",
                            color: "var(--text-primary)",
                            fontSize: "18px",
                            fontWeight: "800",
                          }}
                        >
                          No appointments found
                        </h3>
                        <p
                          style={{
                            color: "var(--text-muted)",
                            fontSize: "15px",
                            fontWeight: "500",
                            margin: 0,
                          }}
                        >
                          {appointments.length === 0
                            ? "This patient has no appointments scheduled yet."
                            : "No appointments match the selected filters."}
                        </p>
                      </div>
                    );
                  }

                  return (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      {filteredAppointments.map((appt) => {
                        const apptDate = appt.start?.toDate
                          ? appt.start.toDate()
                          : new Date(appt.start);
                        const statusColor = getAppointmentStatusColor(
                          appt.status
                        );
                        const statusText = getAppointmentStatusLabel(
                          appt.status
                        );

                        return (
                          <div
                            key={appt.id}
                            style={{
                              border: "1px solid var(--border-color)",
                              borderRadius: "8px",
                              padding: "16px",
                              backgroundColor: "var(--card-background)",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                            }}
                            onClick={() => navigate(`/appointments/${appt.id}`)}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor =
                                "var(--primary-color)";
                              e.currentTarget.style.boxShadow =
                                "0 2px 8px rgba(0,0,0,0.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor =
                                "var(--border-color)";
                              e.currentTarget.style.boxShadow = "none";
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "start",
                                marginBottom: "12px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <span
                                  className="material-symbols-outlined"
                                  style={{ color: "var(--primary-color)" }}
                                >
                                  event
                                </span>
                                <span
                                  style={{
                                    fontSize: "16px",
                                    fontWeight: "700",
                                    color: "var(--text-primary)",
                                  }}
                                >
                                  {apptDate.toLocaleDateString("en-US", {
                                    weekday: "short",
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  })}
                                </span>
                              </div>
                              <span
                                style={{
                                  padding: "4px 12px",
                                  borderRadius: "12px",
                                  fontSize: "12px",
                                  fontWeight: "700",
                                  color: statusColor,
                                  backgroundColor: `${statusColor}15`,
                                  border: `1px solid ${statusColor}30`,
                                }}
                              >
                                {statusText}
                              </span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "8px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <span
                                  className="material-symbols-outlined"
                                  style={{
                                    fontSize: "18px",
                                    color: "var(--text-muted)",
                                  }}
                                >
                                  schedule
                                </span>
                                <span
                                  style={{
                                    fontSize: "14px",
                                    color: "var(--text-secondary)",
                                    fontWeight: "500",
                                  }}
                                >
                                  {apptDate.toLocaleTimeString("en-US", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    hour12: true,
                                  })}
                                </span>
                              </div>
                              {appt.complaint && (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "start",
                                    gap: "8px",
                                  }}
                                >
                                  <span
                                    className="material-symbols-outlined"
                                    style={{
                                      fontSize: "18px",
                                      color: "var(--text-muted)",
                                    }}
                                  >
                                    description
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "14px",
                                      color: "var(--text-secondary)",
                                      fontWeight: "500",
                                      flex: 1,
                                    }}
                                  >
                                    {appt.complaint}
                                  </span>
                                </div>
                              )}
                              {appt.notes && (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "start",
                                    gap: "8px",
                                  }}
                                >
                                  <span
                                    className="material-symbols-outlined"
                                    style={{
                                      fontSize: "18px",
                                      color: "var(--text-muted)",
                                    }}
                                  >
                                    note
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "14px",
                                      color: "var(--text-secondary)",
                                      fontWeight: "500",
                                      flex: 1,
                                    }}
                                  >
                                    {appt.notes}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()
              )}

              <div
                className="edit-patient-actions-new"
                style={{ marginTop: "24px" }}
              >
                <button
                  type="button"
                  className="edit-patient-action-btn edit-patient-action-secondary"
                  onClick={() => navigate("/patients")}
                >
                  <span className="material-symbols-outlined">arrow_back</span>
                  Back to Patients
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Status Tab */}
        {activeTab === "status" && (
          <form className="edit-patient-form-new" onSubmit={handleSaveStatus}>
            <div className="edit-patient-form-card">
              <div className="edit-patient-section-header">
                <span className="material-symbols-outlined">update</span>
                <h3>Update Patient Status</h3>
              </div>

              <div className="edit-patient-field-new">
                <label className="edit-patient-label-new">
                  <span className="material-symbols-outlined">person</span>
                  Patient Name
                </label>
                <div className="edit-patient-readonly-field">
                  {patient.name || "Unknown Patient"}
                </div>
              </div>

              <div className="edit-patient-field-new">
                <label className="edit-patient-label-new">
                  <span className="material-symbols-outlined">info</span>
                  Mark as Recovered
                </label>
                <div className="edit-patient-readonly-field">
                  Patient will be marked as Recovered
                </div>
              </div>

              {error && (
                <div className="edit-patient-error">
                  <span className="material-symbols-outlined">error</span>
                  {error}
                </div>
              )}

              <div className="edit-patient-actions-new">
                <button
                  type="button"
                  className="edit-patient-action-btn edit-patient-action-secondary"
                  onClick={() => navigate("/patients")}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="edit-patient-action-btn edit-patient-action-primary"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="material-symbols-outlined">
                        hourglass_empty
                      </span>
                      Updating...
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined">
                        check_circle
                      </span>
                      Update Status
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        )}

        {/* Contact Information Tab */}
        {activeTab === "contact" && (
          <form className="edit-patient-form-new" onSubmit={handleSaveClinical}>
            <div className="edit-patient-form-card">
              <div className="edit-patient-section-header">
                <span className="material-symbols-outlined">contacts</span>
                <h3>Contact Information</h3>
              </div>

              <div className="edit-patient-field-new">
                <label className="edit-patient-label-new">
                  <span className="material-symbols-outlined">person</span>
                  Patient Name
                </label>
                <div className="edit-patient-readonly-field">
                  {patient.name || "Unknown Patient"}
                </div>
              </div>

              <div className="edit-patient-field-new">
                <label className="edit-patient-label-new">
                  <span className="material-symbols-outlined">phone</span>
                  Phone Number
                </label>
                <input
                  type="tel"
                  className="edit-patient-input-new"
                  placeholder="(555) 123-4567"
                  value={form.phone}
                  onChange={handleChange("phone")}
                />
                <p
                  style={{
                    marginTop: "6px",
                    fontSize: "12px",
                    color: "var(--text-muted)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "14px" }}
                  >
                    info
                  </span>
                  Patient's contact phone number
                </p>
              </div>

              <div className="edit-patient-field-new">
                <label className="edit-patient-label-new">
                  <span className="material-symbols-outlined">email</span>
                  Email Address{" "}
                  <span style={{ color: "#999", fontSize: "12px" }}>
                    (Optional)
                  </span>
                </label>
                <input
                  type="email"
                  className="edit-patient-input-new"
                  placeholder="patient@example.com"
                  value={form.email}
                  onChange={handleChange("email")}
                />
                <p
                  style={{
                    marginTop: "6px",
                    fontSize: "12px",
                    color: "var(--text-muted)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "14px" }}
                  >
                    info
                  </span>
                  Patient's email address
                </p>
              </div>

              <div className="edit-patient-field-new">
                <label className="edit-patient-label-new">
                  <span className="material-symbols-outlined">location_on</span>
                  Address{" "}
                  <span style={{ color: "#999", fontSize: "12px" }}>
                    (Optional)
                  </span>
                </label>
                <textarea
                  className="edit-patient-textarea-new"
                  placeholder="Street address, City, State, ZIP code"
                  value={form.address}
                  onChange={handleChange("address")}
                  rows={3}
                />
                <p
                  style={{
                    marginTop: "6px",
                    fontSize: "12px",
                    color: "var(--text-muted)",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "14px" }}
                  >
                    info
                  </span>
                  Patient's residential address
                </p>
              </div>

              {error && (
                <div className="edit-patient-error">
                  <span className="material-symbols-outlined">error</span>
                  {error}
                </div>
              )}

              <div className="edit-patient-actions-new">
                <button
                  type="button"
                  className="edit-patient-action-btn edit-patient-action-secondary"
                  onClick={() => navigate("/patients")}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="edit-patient-action-btn edit-patient-action-primary"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="material-symbols-outlined">
                        hourglass_empty
                      </span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined">save</span>
                      Save Contact Info
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function getStatusColor(status) {
  if (!status) return "#60a5fa";
  const s = String(status).toLowerCase();
  if (s.includes("active")) return "#22c55e";
  if (s.includes("recover")) return "#9ca3af";
  if (s.includes("new") || s.includes("pending")) return "#f59e0b";
  return "#60a5fa";
}

function getAppointmentStatusColor(status) {
  if (!status) return "#06b6d4";
  const s = status.toLowerCase();
  if (s.includes("confirm")) return "#06b6d4";
  if (s.includes("pending")) return "#f59e0b";
  if (s.includes("cancel")) return "#ef4444";
  if (s.includes("complete")) return "#10b981";
  return "#06b6d4";
}

function getAppointmentStatusLabel(status) {
  if (!status) return "Confirmed";
  const s = status.toLowerCase();
  if (s.includes("confirm")) return "Confirmed";
  if (s.includes("pending")) return "Pending";
  if (s.includes("cancel")) return "Cancelled";
  if (s.includes("complete")) return "Completed";
  return status;
}

export default EditPatientPage;
